import { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { ROUTES } from 'constants/RoutePaths';
import { userToken } from '_actions/user_actions';
import TSInfoAlert from 'components/common/InfoAlert/InfoAlert';
import MLModal from 'components/common/Modal/Modal';
import EmergencyContact from 'pages/Protected/User/EmergencyContact/EmergencyContact';
import MLToast from 'components/common/Toast/Toast';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetUserDetailsQuery } from 'app/api/usersApiSlice';
import {
  useCreateActivityMutation,
  useCreateCheckinMutation,
} from 'app/api/activityApiSlice';
import TSButton from 'components/common/Button/Button';
import TSCounter from 'components/common/Counter/Counter';
import LogSuccessMsg from './LogSuccessMessage';
import MLDialog from 'components/common/Dialog/Dialog';

const LogActivity = ({
  option,
  toggleModal,
  checkinPointDetails = null,
  isCheckin = false,
}) => {
  const userEmail = useSelector((state) => state.global?.userEmail);
  const navigate = useNavigate();
  const [value, setValue] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [additionalWalkers, setAdditionalWalkers] = useState(0);
  const [isAuth, setIsAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [emCont, setEmCont] = useState([]);
  const [currActivity, setCurrActivity] = useState(null);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);

  const QRCodeId = useSelector((state) => state.global.qrCodeId);

  const { showToast } = MLToast();

  const { data: userData, isLoading: isUserLoading } = useGetUserDetailsQuery(
    userEmail,
    { skip: !userEmail }
  );

  useEffect(() => {
    const checkAuth = async () => {
      const t = await userToken();
      setIsAuth(!!t);
    };
    checkAuth();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (userData) {
      if (userData?.[0]?.emergency_contacts) {
        setEmCont([...userData?.[0]?.emergency_contacts]);
      }
      if (userData?.[0]?.active_checkin) {
        setCurrActivity([...userData?.[0]?.active_checkin]);
      }
    }
  }, [userData]);

  const [
    createActivity,
    {
      data: createActivityData,
      isLoading: isCreateActivityLoading,
      isError: isCreateActivityError,
      isSuccess: isCreateActivitySuccess,
    },
  ] = useCreateActivityMutation();

  const [
    createCheckin,
    {
      data: createCheckinData,
      isLoading: isCreateCheckinLoading,
      isError: isCreateCheckinError,
      isSuccess: isCreateCheckinSuccess,
    },
  ] = useCreateCheckinMutation();

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const toggleContactModal = (state) => {
    setIsModalOpen(state);
  };

  const LogComplete = () => {
    toggleModal(false);
    setShowSuccessMsg(false);
    navigate(`/${ROUTES.HOME}`);
  };

  // The walker and the start/check-in times come from the server. qr_code is
  // the scanned sign's permanent code, if the walker came from one.
  const handleLogActivity = async () => {
    const res = await createActivity({
      trail_option_id: option?.TrailOptionID ?? option.trailoptionid,
      emergency_contact_id: value,
      participants: additionalWalkers,
      qr_code: QRCodeId,
    });
    if (res.data?.[0]?.is_success) {
      //nav to activity page or show success message
      showToast('Activity logged successfully!', 'success');
      setShowSuccessMsg(true);
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  const handleLogCheckin = async () => {
    const res = await createCheckin({
      activity_id: currActivity?.[0]?.ActivityID,
      trail_option_id: option?.TrailOptionID ?? option.trailoptionid,
      qr_code: QRCodeId,
    });
    if (res.data?.[0]?.is_success) {
      toggleModal(false);
      //nav to activity page or show success message
      showToast('Checkin logged successfully!', 'success');
      navigate(`/${ROUTES.HOME}`);
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  if (isUserLoading || isLoading) {
    return <div>Loading...</div>;
  } else {
    const defaultContact = emCont.find((contact) => contact.IsPrimary);
    if (defaultContact && !value) {
      setValue(defaultContact.EmergencyContactID);
    }
    return (
      <>
        {isCheckin && currActivity ? (
          <div>
            <Typography variant="h2">Log Check-in</Typography>
            <Typography variant="body1">
              You're at {checkinPointDetails?.checkinpointlocation}, heading to{' '}
              {option ? option.TrailOptionName : 'No option selected'}
            </Typography>
            {currActivity[0].ParkID !== checkinPointDetails?.parkid && (
              <TSInfoAlert
                info1={
                  'Checkin location does not match the location of your current activity!'
                }
                info2={`${checkinPointDetails?.checkinpointlocation} is in ${checkinPointDetails?.parkname}, but you have a current activity in ${currActivity[0].ParkName}. If you have already left the previous park, please end the previous activity and start a new one for better tracking.`}
                alertType={`error-alert`}
                alertIcon={'Error-tick'}
              />
            )}
            {currActivity[0].ParkID === checkinPointDetails?.parkid &&
              currActivity[0].TrailID !== checkinPointDetails?.trailid && (
                <TSInfoAlert
                  info1={
                    'Checkin trail does not match the trail of your current activity!'
                  }
                  info2={
                    'The check-in will still be logged but please be cautious and make sure you are on the right trail.'
                  }
                  alertType={`warning-alert`}
                  alertIcon={'Warning'}
                />
              )}
            <div className="flex gap-4 mt-4">
              <TSButton
                btnText="Confirm Check-in"
                classList="py-4"
                onClickFunc={() => handleLogCheckin()}
              />
              <TSButton
                btnText="Cancel"
                btnVariant="outlined"
                classList="py-4"
                onClickFunc={() => toggleModal(false)}
              />
            </div>
          </div>
        ) : (
          <div>
            <Typography variant="h2">Log Activity</Typography>
            <div className="flex flex-col gap-2 my-4">
              {emCont.length > 0 && (
                <>
                  <Typography variant="body1">
                    Please select an emergency contact for us to notify in case
                    of an emergency during your walk. We will also log your
                    activity and location for safety purposes.
                  </Typography>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={value}
                    onChange={handleChange}
                  >
                    {emCont.length > 0 &&
                      emCont
                        .sort((a, b) =>
                          a.ContactName.localeCompare(b.ContactName)
                        )
                        .map((contact, index) => {
                          return (
                            <FormControlLabel
                              key={index}
                              value={contact.EmergencyContactID}
                              control={<Radio />}
                              label={contact.ContactName}
                            />
                          );
                        })}
                  </RadioGroup>
                </>
              )}
              <TSButton
                btnText="Add Contact"
                classList="py-4"
                onClickFunc={() => toggleContactModal(true)}
              />
              <div className="flex flex-col gap-2 items-center mt-4">
                <Typography variant="body1">
                  If you are walking with others, please let us know how many
                  additional walkers are with you (not including yourself) who
                  are NOT logging an activity themselves.
                </Typography>
                <div className="flex flex-col gap-2 items-center">
                  <TSCounter
                    count={additionalWalkers}
                    setCount={setAdditionalWalkers}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-12">
              <TSButton
                btnText={
                  isCreateActivityLoading
                    ? 'Logging...'
                    : !value
                      ? 'Select a contact to log activity'
                      : 'Log Activity'
                }
                classList="py-4"
                btnDisable={!value || isCreateActivityLoading}
                onClickFunc={() => handleLogActivity()}
              />
              <TSButton
                btnText="Cancel"
                btnVariant="outlined"
                classList="py-4"
                onClickFunc={() => toggleModal(false)}
              />
            </div>
          </div>
        )}
        <MLModal open={isModalOpen} setOpenModal={setIsModalOpen}>
          <EmergencyContact
            contact={null}
            index={99}
            isModal={true}
            toggleModal={setIsModalOpen}
          />
        </MLModal>
        <MLDialog openDialog={showSuccessMsg} setOpenDialog={setShowSuccessMsg}>
          <LogSuccessMsg
            handleDismiss={() => LogComplete()}
            safetySheetUrl={option?.SafetySheetURL ?? option?.safetysheeturl}
          />
        </MLDialog>
      </>
    );
  }
};

export default LogActivity;
